import React, { useState } from 'react';
import { View, TouchableOpacity, TextInput, ScrollView, ActivityIndicator } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import getBestContrastColor from '../../../services/best-contrast-color';

export interface FilterListItem {
    label: string;
    value: number;
    color?: string;
}

interface Props {
    items: FilterListItem[];
    value: number[];
    setValue: React.Dispatch<React.SetStateAction<number[]>>;
    renderBadge?: boolean;
    label?: string;
    isLoading?: boolean;
}

function FilterCheckboxList({ items, value, setValue, renderBadge, label, isLoading }: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const [search, setSearch] = useState('');

    const filtered = items.filter(item =>
        item.label.toLowerCase().includes(search.toLowerCase())
    );

    const toggleItem = (id: number) => {
        if (value.includes(id)) {
            setValue(value.filter(v => v !== id));
        } else {
            setValue([...value, id]);
        }
    };

    return (
        <View style={styles.container}>
            {label && <MyAppText style={styles.filterLabel}>{label}</MyAppText>}

            {items.length >= 7 && (
                <View style={styles.searchContainer}>
                    <FontAwesomeIcon icon={faMagnifyingGlass as IconDefinition} fontSize={16} color={theme.colors.neutral['300']} />
                    <TextInput
                        style={styles.searchInput}
                        placeholder={translate('typeToSearch')}
                        placeholderTextColor={theme.colors.neutral['300']}
                        value={search}
                        onChangeText={setSearch}
                    />
                </View>
            )}

            <View style={styles.countRow}>
                <MyAppText style={styles.countText}>
                    {translate('items')}: {filtered.length}
                </MyAppText>
                {value.length === 0 ? (
                    <TouchableOpacity onPress={() => setValue(filtered.map(i => i.value))}>
                        <MyAppText style={styles.actionText}>
                            {translate('selectAll')}
                        </MyAppText>
                    </TouchableOpacity>
                ) : (
                    <TouchableOpacity onPress={() => setValue([])}>
                        <MyAppText style={styles.actionText}>
                            {translate('clearSelection')}
                        </MyAppText>
                    </TouchableOpacity>
                )}
            </View>

            {isLoading ? (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size='small' color={theme.colors.primary['500']} />
                </View>
            ) : (
                <ScrollView
                    style={styles.list}
                    contentContainerStyle={{ gap: 12 }}
                    nestedScrollEnabled
                >
                    {filtered.map((item) => {
                        const selected = value.includes(item.value);
                        return (
                            <TouchableOpacity
                                key={item.value}
                                style={styles.row}
                                onPress={() => toggleItem(item.value)}
                            >
                                <View style={[styles.checkbox, selected && styles.checkboxSelected]}>
                                    {selected && <FontAwesomeIcon icon={faCheck as IconDefinition} fontSize={10} color={theme.colors.white['500']} />}
                                </View>
                                {renderBadge ? (
                                    <View style={[styles.badge, { backgroundColor: item.color ?? theme.colors.neutral['300'] }]}>
                                        <MyAppText style={styles.badgeText(item.color ?? theme.colors.neutral['300'])}>{item.label}</MyAppText>
                                    </View>
                                ) : (
                                    <MyAppText style={styles.label}>{item.label}</MyAppText>
                                )}
                            </TouchableOpacity>
                        );
                    })}
                </ScrollView>
            )}
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: { gap: 12 },
    filterLabel: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.labelColor,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        gap: 8,
    },
    searchInput: {
        flex: 1,
        fontSize: 16,
        color: theme.colors.neutral['400'],
        outlineStyle: 'none',
    },
    countRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    countText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.labelColor,
    },
    actionText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
    },
    loadingContainer: {
        paddingVertical: 20,
        alignItems: 'center',
        justifyContent: 'center',
    },
    list: {
        maxHeight: 208,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    checkbox: {
        width: 20,
        height: 20,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        alignItems: 'center',
        justifyContent: 'center',
    },
    checkboxSelected: {
        backgroundColor: theme.colors.primary['500'],
        borderColor: theme.colors.primary['500'],
    },
    badge: {
        paddingHorizontal: 12,
        paddingVertical: 4,
        borderRadius: 20,
    },
    badgeText: (tagColor: string) => ({
        color: getBestContrastColor(tagColor),
        fontSize: 14,
        fontWeight: '600',
    }),
    label: {
        fontSize: 16,
        color: theme.colors.labelColor,
    },
}));

export default FilterCheckboxList;
